import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Code, MessageSquare, Shield, Menu, CheckCircle, Zap } from 'lucide-react';
import { Button } from './ui/Button';
import Sidebar from './layout/Sidebar';
import ThemeToggle from './ThemeToggle';
import { useAuth } from '../contexts/AuthContext';

const LandingPage = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { currentUser, isAnonymous } = useAuth();
  
  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  return (
    <div className="flex h-screen">
      {/* Sidebar */}
      <Sidebar isOpen={sidebarOpen} toggleSidebar={toggleSidebar} />
      
      {/* Main Content */}
      <div className="flex-1 flex flex-col md:ml-64 bg-background">
        {/* Top Navigation Bar */}
        <header className="sticky top-0 z-10 bg-background/80 backdrop-blur-sm border-b border-border shadow-sm">
          <div className="flex items-center justify-between px-4 py-3">
            <div className="flex items-center">
              <Button
                variant="ghost"
                size="icon"
                className="md:hidden mr-2"
                onClick={toggleSidebar}
              >
                <Menu className="h-6 w-6" />
                <span className="sr-only">Toggle sidebar</span>
              </Button>
            </div>
            <div className="flex items-center gap-4">
              {/* Show login/signup buttons for anonymous users */}
              {currentUser && isAnonymous && (
                <div className="flex gap-2">
                  <Button variant="outline" asChild>
                    <Link to="/login">Log in</Link>
                  </Button>
                  <Button asChild>
                    <Link to="/register">Sign up</Link>
                  </Button>
                </div>
              )}
              <ThemeToggle />
            </div>
          </div>
        </header>
        
        {/* Page Content */}
        <div className="flex-1 overflow-y-auto">
          <div className="flex flex-col">
            
            {/* Hero Section - Above the Fold */}
            <section className="relative min-h-[90vh] flex items-center justify-center px-4 py-20">
              {/* Background gradient */}
              <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-background to-secondary/5"></div>
              
              <div className="container mx-auto max-w-7xl relative z-10">
                <div className="grid lg:grid-cols-2 gap-12 items-center">
                  
                  {/* Left side - Content */}
                  <div className="flex flex-col space-y-8">
                    {/* Title - Own your niche */}
                    <div className="space-y-4">
                      <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-foreground leading-tight">
                        Transform AI Text Into 
                        <span className="bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent"> Natural</span> Human Writing
                      </h1>
                    </div>
                    
                    {/* Subtitle - How you create value */}
                    <p className="text-xl md:text-2xl text-muted-foreground leading-relaxed">
                      Advanced tone and style enhancement that makes your AI-assisted content sound naturally human. Perfect for busy writers who need authentic, engaging copy fast.
                    </p>
                    
                    {/* CTA - Value focused with objection handling */}
                    <div className="flex flex-col sm:flex-row gap-4">
                      <Button 
                        size="lg" 
                        className="bg-primary hover:bg-primary/90 text-primary-foreground px-8 py-4 text-lg font-semibold"
                        asChild
                      >
                        <Link to="/app">
                          Enhance Your Writing for Free
                          <ArrowRight className="ml-2 h-5 w-5" />
                        </Link>
                      </Button>
                    </div>
                  </div>
                  
                  {/* Right side - Product in action visual */}
                  <div className="lg:pl-8">
                    <div className="relative">
                      {/* Main demo container */}
                      <div className="bg-card border border-border rounded-2xl shadow-2xl overflow-hidden">
                        {/* Header */}
                        <div className="bg-muted/50 px-6 py-4 border-b border-border">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-3">
                              <div className="flex space-x-1">
                                <div className="w-3 h-3 rounded-full bg-red-500"></div>
                                <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                                <div className="w-3 h-3 rounded-full bg-green-500"></div>
                              </div>
                              <span className="font-medium text-foreground">AI Humanizer</span>
                            </div>
                            <div className="flex items-center space-x-2 text-sm text-muted-foreground">
                              <div className="w-2 h-2 rounded-full bg-green-500"></div>
                              <span>Live</span>
                            </div>
                          </div>
                        </div>
                        
                        {/* Content */}
                        <div className="p-6 space-y-6">
                          {/* Before - AI text */}
                          <div className="space-y-3">
                            <div className="flex items-center space-x-2">
                              <div className="w-8 h-8 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
                                <Code className="w-4 h-4 text-red-600 dark:text-red-400" />
                              </div>
                              <span className="font-medium text-foreground">AI-Assisted Text</span>
                              <div className="px-2 py-1 bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 text-xs rounded-full">
                                Robotic Tone
                              </div>
                            </div>
                            <div className="bg-muted/50 border border-border rounded-lg p-4">
                              <p className="text-foreground text-sm leading-relaxed">
                                "The implementation of this solution will significantly optimize operational efficiency through the utilization of advanced technological frameworks that enhance productivity and streamline processes."
                              </p>
                            </div>
                          </div>
                          
                          {/* Transformation arrow */}
                          <div className="flex justify-center">
                            <div className="w-12 h-12 rounded-full bg-gradient-to-r from-primary to-secondary flex items-center justify-center">
                              <Zap className="w-6 h-6 text-white" />
                            </div>
                          </div>
                          
                          {/* After - Humanized text */}
                          <div className="space-y-3">
                            <div className="flex items-center space-x-2">
                              <div className="w-8 h-8 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                                <MessageSquare className="w-4 h-4 text-green-600 dark:text-green-400" />
                              </div>
                              <span className="font-medium text-foreground">Human-Style Writing</span>
                              <div className="px-2 py-1 bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400 text-xs rounded-full">
                                Natural & Engaging
                              </div>
                            </div>
                            <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
                              <p className="text-foreground text-sm leading-relaxed">
                                "This solution will help your team work much more efficiently. The new technology makes everything faster and easier, so you can focus on what really matters."
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      {/* Floating elements */}
                      <div className="absolute -top-4 -right-4 w-24 h-24 bg-gradient-to-r from-primary to-secondary rounded-full opacity-20 blur-xl"></div>
                      <div className="absolute -bottom-4 -left-4 w-32 h-32 bg-gradient-to-r from-secondary to-primary rounded-full opacity-20 blur-xl"></div>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* Below the fold content */}
            
            {/* Problem/Solution Section */}
            <section id="demo" className="py-20 px-4 bg-muted/30">
              <div className="container mx-auto max-w-6xl">
                <div className="text-center mb-16">
                  <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-6">
                    Stop Wasting Time on Robotic AI Writing
                  </h2>
                  <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
                    Your AI-generated content sounds stiff and artificial. Whether it's blog posts, marketing copy, or reports—you need writing that connects with real people.
                  </p>
                </div>
                
                <div className="grid md:grid-cols-2 gap-12 items-center">
                  <div>
                    <div className="space-y-6">
                      <div className="flex items-start space-x-4">
                        <div className="w-8 h-8 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center flex-shrink-0 mt-1">
                          <span className="text-red-600 dark:text-red-400 font-bold">✗</span>
                        </div>
                        <div>
                          <h3 className="font-semibold text-foreground mb-2">Sounds Too Formal</h3>
                          <p className="text-muted-foreground">AI writing often feels stiff, academic, and disconnected from your audience</p>
                        </div>
                      </div>
                      
                      <div className="flex items-start space-x-4">
                        <div className="w-8 h-8 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center flex-shrink-0 mt-1">
                          <span className="text-red-600 dark:text-red-400 font-bold">✗</span>
                        </div>
                        <div>
                          <h3 className="font-semibold text-foreground mb-2">Lacks Personality</h3>
                          <p className="text-muted-foreground">Generic, repetitive patterns that don't reflect your unique voice and style</p>
                        </div>
                      </div>
                      
                      <div className="flex items-start space-x-4">
                        <div className="w-8 h-8 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center flex-shrink-0 mt-1">
                          <span className="text-red-600 dark:text-red-400 font-bold">✗</span>
                        </div>
                        <div>
                          <h3 className="font-semibold text-foreground mb-2">Time-Consuming Edits</h3>
                          <p className="text-muted-foreground">Hours spent manually polishing AI content to make it engaging and authentic</p>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div>
                    <div className="space-y-6">
                      <div className="flex items-start space-x-4">
                        <div className="w-8 h-8 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center flex-shrink-0 mt-1">
                          <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400" />
                        </div>
                        <div>
                          <h3 className="font-semibold text-foreground mb-2">Conversational & Engaging</h3>
                          <p className="text-muted-foreground">Transform formal AI text into natural, reader-friendly content that connects</p>
                        </div>
                      </div>
                      
                      <div className="flex items-start space-x-4">
                        <div className="w-8 h-8 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center flex-shrink-0 mt-1">
                          <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400" />
                        </div>
                        <div>
                          <h3 className="font-semibold text-foreground mb-2">Your Authentic Voice</h3>
                          <p className="text-muted-foreground">Maintains your message while adding personality and human warmth</p>
                        </div>
                      </div>
                      
                      <div className="flex items-start space-x-4">
                        <div className="w-8 h-8 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center flex-shrink-0 mt-1">
                          <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400" />
                        </div>
                        <div>
                          <h3 className="font-semibold text-foreground mb-2">Instant Results</h3>
                          <p className="text-muted-foreground">Polish thousands of words in seconds, not hours of manual editing</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="mt-12 text-center">
                  <Button size="lg" className="bg-primary hover:bg-primary/90 text-primary-foreground px-8 py-4" asChild>
                    <Link to="/app">Try It For Free</Link>
                  </Button>
                </div>
              </div>
            </section>

            {/* Features Grid */}
            <section className="py-20 px-4 bg-background">
              <div className="container mx-auto max-w-6xl">
                <div className="text-center mb-16">
                  <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-6">
                    Why Writers Choose ThatAIHumanizer
                  </h2>
                  <p className="text-xl text-muted-foreground">
                    The smartest way to enhance AI-assisted writing
                  </p>
                </div>
                
                <div className="grid md:grid-cols-3 gap-8">
                  <div className="bg-card border border-border p-8 rounded-2xl shadow-sm hover:shadow-md transition-shadow">
                    <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center mb-6">
                      <Shield className="h-6 w-6 text-primary" />
                    </div>
                    <h3 className="text-xl font-semibold mb-4 text-card-foreground">Professional Quality</h3>
                    <p className="text-muted-foreground leading-relaxed">
                      Transform AI drafts into polished, professional content that sounds authentically human and engaging.
                    </p>
                  </div>
                  
                  <div className="bg-card border border-border p-8 rounded-2xl shadow-sm hover:shadow-md transition-shadow">
                    <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center mb-6">
                      <Zap className="h-6 w-6 text-primary" />
                    </div>
                    <h3 className="text-xl font-semibold mb-4 text-card-foreground">Lightning Fast</h3>
                    <p className="text-muted-foreground leading-relaxed">
                      Save hours of editing time. Transform thousands of words in seconds with intelligent tone enhancement.
                    </p>
                  </div>
                  
                  <div className="bg-card border border-border p-8 rounded-2xl shadow-sm hover:shadow-md transition-shadow">
                    <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center mb-6">
                      <MessageSquare className="h-6 w-6 text-primary" />
                    </div>
                    <h3 className="text-xl font-semibold mb-4 text-card-foreground">Preserves Your Message</h3>
                    <p className="text-muted-foreground leading-relaxed">
                      Advanced rewriting keeps your core ideas intact while making the tone more natural and conversational.
                    </p>
                  </div>
                </div>
              </div>
            </section>
            
            {/* Final CTA Section */}
            <section className="py-20 px-4 bg-primary/10 dark:bg-gradient-to-r dark:from-primary dark:to-secondary">
              <div className="container mx-auto max-w-4xl text-center">
                <h2 className="text-3xl md:text-4xl font-bold mb-6 text-foreground dark:text-white">
                  Ready to Write Like a Human?
                </h2>
                <p className="text-xl mb-8 text-muted-foreground dark:text-white/90 max-w-2xl mx-auto">
                  Join thousands of writers, content creators, and busy professionals who use ThatAIHumanizer to transform AI drafts into engaging, authentic content.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Button 
                    size="lg" 
                    className="bg-primary text-primary-foreground hover:bg-primary/90 dark:bg-white dark:text-primary dark:hover:bg-white/90 px-8 py-4 text-lg font-semibold"
                    asChild
                  >
                    <Link to="/app">Start Writing Better Today</Link>
                  </Button>
                  <Button 
                    size="lg" 
                    variant="outline" 
                    className="border-primary text-primary hover:bg-primary/10 dark:border-white dark:text-white dark:hover:bg-white/10 px-8 py-4 text-lg"
                    asChild
                  >
                    <Link to="/register">Create Account</Link>
                  </Button>
                </div>
                <p className="text-sm text-muted-foreground dark:text-white/80 mt-6">
                  No credit card required • 1,000 words free • Instant access
                </p>
              </div>
            </section>
            
            {/* Footer */}
            <footer className="py-12 px-4 bg-muted/30 border-t border-border">
              <div className="container mx-auto max-w-6xl">
                <div className="flex flex-col md:flex-row justify-between items-center">
                  <div className="flex items-center mb-6 md:mb-0">
                    <img src={process.env.PUBLIC_URL + "/thataihumanizer.svg"} alt="Thataihumanizer Logo" className="h-8 w-8 mr-3" />
                    <span className="font-bold text-xl text-primary">ThatAIHumanizer</span>
                  </div>
                </div>
                
                <div className="mt-8 pt-8 border-t border-border flex flex-col md:flex-row justify-between items-center">
                  <div className="text-sm text-muted-foreground mb-4 md:mb-0">
                    © {new Date().getFullYear()} ThatAIHumanizer. All rights reserved.
                  </div>
                  <div className="flex space-x-6 text-sm text-muted-foreground">
                    <Link to="/privacy" className="hover:text-foreground transition-colors">Privacy Policy</Link>
                    <Link to="/terms" className="hover:text-foreground transition-colors">Terms of Service</Link>
                    <Link to="/contact" className="hover:text-foreground transition-colors">Contact</Link>
                  </div>
                </div>
              </div>
            </footer>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LandingPage; 